import { useRef, useState, useCallback, useEffect } from "react";

import { useLockBodyScroll } from "@narmi/design_system";

import { getValidShortcutKey } from "./utils";

type SearchShortcutKeys = {
  k: boolean;
  meta: boolean;
};

export const useSearchBarListeners = () => {
  const pressedKeys = useRef<SearchShortcutKeys>({ k: false, meta: false });
  const paneRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  useLockBodyScroll(isOpen);

  const toggleOpen = useCallback(() => setIsOpen((state) => !state), []);
  const setClosed = useCallback(() => setIsOpen(false), []);

  const downHandler = useCallback(
    (e: KeyboardEvent) => {
      // Exit early for held keys
      if (e.repeat) {
        return;
      }

      const validKey = getValidShortcutKey(e);
      if (validKey) {
        pressedKeys.current = {
          ...pressedKeys.current,
          [validKey]: true,
        };
      }

      if (pressedKeys.current.k && pressedKeys.current.meta) {
        toggleOpen();
      } else if (e.key === "Escape") {
        setClosed();
      }
    },
    [toggleOpen, setClosed]
  );

  const upHandler = useCallback((e: KeyboardEvent) => {
    const validKey = getValidShortcutKey(e);
    if (validKey) {
      pressedKeys.current = {
        ...pressedKeys.current,
        [validKey]: false,
      };
    }
  }, []);

  const clickHandler = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (paneRef.current && target && !paneRef.current.contains(target)) {
        setClosed();
      }
    },
    [setClosed]
  );

  useEffect(() => {
    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);
    window.addEventListener("mousedown", clickHandler);

    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
      window.removeEventListener("mousedown", clickHandler);
    };
  }, [downHandler, upHandler, clickHandler]);

  return { isOpen, toggleOpen, setClosed, paneRef };
};
